import { useApiQuery } from '../use-api.js';
import { VerdictPill } from '../components/VerdictPill.js';
import type { CoverageResponse } from '../lib/types.js';

export function Coverage() {
  const query = useApiQuery<CoverageResponse>(['coverage'], '/api/test-env-coverage');

  if (query.isLoading) return <p>Loading coverage…</p>;
  if (query.isError || !query.data) return <p className="error">Failed to load coverage.</p>;

  const { repos, counts } = query.data;
  const countEntries = Object.entries(counts ?? {});

  return (
    <section>
      <h2>Test-env coverage</h2>

      <p className="section-intro">
        The readiness verdict of each repo's in-container test environment, taken from
        its most recent Borg run. It reflects whether dependencies installed and the
        test database came up — a signal for whether Borg can actually run that repo's
        tests, independent of any individual run's outcome.
      </p>

      <dl className="legend">
        <dt><VerdictPill verdict="READY" /></dt>
        <dd>At least one component set up and none failed — tests can run.</dd>
        <dt><VerdictPill verdict="DEGRADED" /></dt>
        <dd>At least one component failed to set up (a dependency install or the test DB), so tests may not run reliably.</dd>
        <dt><VerdictPill verdict="NOT_APPLICABLE" /></dt>
        <dd>Nothing to set up — no dependencies and no test database for this repo.</dd>
        <dt><VerdictPill verdict="UNKNOWN" /></dt>
        <dd>Readiness couldn't be captured for the latest run.</dd>
      </dl>

      <div className="counts">
        {countEntries.length === 0 && <span className="empty">No counts.</span>}
        {countEntries.map(([verdict, n]) => (
          <span key={verdict} className="count-chip">
            <VerdictPill verdict={verdict} /> <strong>{n}</strong>
          </span>
        ))}
      </div>

      <table className="data-table">
        <thead>
          <tr>
            <th>Repo</th>
            <th>Verdict</th>
            <th>Last run</th>
          </tr>
        </thead>
        <tbody>
          {(repos ?? []).length === 0 && (
            <tr><td colSpan={3} className="empty">No repos.</td></tr>
          )}
          {(repos ?? []).map((r) => (
            <tr key={r.repo}>
              <td>{r.repo}</td>
              <td><VerdictPill verdict={r.verdict} /></td>
              <td>{r.created_at ? r.created_at.slice(0, 16) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
